/**
 * Every report the office can ask for.
 *
 * <p>One list, read by the side menu and by the report screen itself, so a
 * report added here appears in both. Keeping two lists is how a report ends up
 * in the menu and nowhere else, or reachable only by somebody who knows the
 * URL.</p>
 */
export type ReportDefinition = {
  /** What the API calls it. */
  type: string
  /** What it is called in the menu, kept short enough to sit in one. */
  name: string
  /** What it is for, in the words an office would use. */
  about: string
  /** Whether the date range means anything to it. */
  dated: boolean
  permission: string
  group: 'Finance' | 'Sales' | 'Operations'
  /**
   * Whether a customer can be picked. Only for reports with a customer on
   * every row; offering it on a staff roster would be a box that does nothing.
   */
  byCustomer?: boolean
  /**
   * The states this report's rows can be in, as the office would say them.
   * Left out, the report has no status worth filtering and none is offered.
   */
  statuses?: { code: string; label: string }[]
  /**
   * Whether a line of business can be picked. Only for reports whose rows
   * carry one; a payments register does not know what was cooked.
   */
  byService?: boolean
  /**
   * Whether the branch in force narrows it. Nearly everything, but a company
   * -wide catalogue or an ageing summary is the whole business by nature, and
   * silently cutting those to one branch would be a wrong total shown as a
   * right one.
   */
  byOutlet?: boolean
  /** Whether the channel a message went on (WhatsApp, email, SMS) can be picked. */
  byChannel?: boolean
}

export const REPORTS: ReportDefinition[] = [
  {
    type: 'SALES_REGISTER',
    name: 'Sales register',
    about: 'Every tax invoice raised in the period with the tax split as it was charged. '
      + 'What a GST return is built from and an audit starts at.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    statuses: [
      { code: 'DRAFT', label: 'Draft' },
      { code: 'ISSUED', label: 'Issued' },
      { code: 'PARTIALLY_PAID', label: 'Part paid' },
      { code: 'PAID', label: 'Paid' },
      { code: 'OVERDUE', label: 'Overdue' },
      { code: 'CANCELLED', label: 'Cancelled' },
    ],
    byOutlet: true,
  },
  {
    type: 'COMPLIMENTARY_REGISTER',
    name: 'Complimentary register',
    about: 'What the business gave away in the period, and what it was worth. Priced out '
      + 'line by line, on its own numbering, and owed by nobody.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    statuses: [
      { code: 'DRAFT', label: 'Draft' },
      { code: 'ISSUED', label: 'Issued' },
      { code: 'CANCELLED', label: 'Cancelled' },
    ],
    byOutlet: true,
  },
  {
    type: 'CUSTOMER_PAYMENTS',
    name: 'Customer Receipts',
    about: 'Every receipt taken, with what is still sitting against no bill.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    statuses: [
      { code: 'RECEIVED', label: 'Received' },
      { code: 'PENDING', label: 'Pending' },
      { code: 'ALLOCATED', label: 'Allocated' },
    ],
    byOutlet: true,
  },
  {
    type: 'REFUND_REGISTER',
    name: 'Refunds',
    about: 'Money going back, and how far along each one is. A refund approved '
      + 'and never paid out is the one worth finding.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    statuses: [
      { code: 'REQUESTED', label: 'Requested' },
      { code: 'APPROVED', label: 'Approved' },
      { code: 'PROCESSED', label: 'Processed' },
      { code: 'REJECTED', label: 'Rejected' },
    ],
    byOutlet: true,
  },
  {
    type: 'WRITE_OFF_REGISTER',
    name: 'Write-offs and rebates',
    about: 'Money the business decided not to collect, each with its voucher and reason.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    statuses: [
      { code: 'REQUESTED', label: 'Awaiting approval' },
      { code: 'ISSUED', label: 'Issued' },
      { code: 'REJECTED', label: 'Rejected' },
    ],
    byOutlet: true,
  },
  {
    type: 'GST_ADVANCES_11A',
    name: 'GST 11A',
    about: 'Advances taken in the period with no invoice raised against them yet. '
      + 'Tax falls due when the money is taken, so it goes into GSTR-1 table 11A.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
  },
  {
    type: 'GST_ADVANCES_11B',
    name: 'GST 11B',
    about: 'Earlier advances against orders invoiced in the period. The invoice carries '
      + 'the whole of the tax, so the advance is reversed out in GSTR-1 table 11B.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
  },
  {
    type: 'CUSTOMER_OUTSTANDING',
    name: 'Customer outstanding',
    about: 'What every customer still owes, oldest first.',
    dated: false,
    permission: 'report.finance',
    group: 'Finance',
  },
  {
    type: 'PAYMENT_AGEING',
    name: 'Payment ageing',
    about: 'Unpaid invoices by how long they have been unpaid.',
    dated: false,
    permission: 'report.finance',
    group: 'Finance',
  },
  {
    type: 'EVENT_PROFITABILITY',
    name: 'Event profitability',
    about: 'What each event was billed against what it cost to put on.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
  },
  {
    type: 'CUSTOMER_REGISTER',
    name: 'Customer details',
    about: 'Customers with events held in the period, what they were billed and what '
      + 'is still owed. Counted on the events held, not on when the name was typed.',
    dated: true,
    permission: 'report.sales',
    group: 'Sales',
    byCustomer: true,
    byOutlet: true,
    byService: true,
  },
  {
    type: 'PACKAGE_CATALOGUE',
    name: 'Packages',
    about: 'Every package on offer by line of business and by hall, with its published '
      + 'per-head price, minimum guests and rate of tax.',
    dated: false,
    permission: 'report.sales',
    group: 'Sales',
    byOutlet: true,
    byService: true,
  },
  {
    type: 'FEEDBACK_REGISTER',
    name: 'Feedback',
    about: 'What guests said, rating by rating, with the comment beside it.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
    byOutlet: true,
    byService: true,
  },
  {
    type: 'ENQUIRY_REGISTER',
    name: 'Enquiry register',
    about: 'Every enquiry taken in the period and where it got to.',
    dated: true,
    permission: 'report.sales',
    group: 'Sales',
  },
  {
    type: 'EVENT_PIPELINE',
    name: 'Event pipeline',
    about: 'Confirmed work ahead, by date.',
    dated: true,
    permission: 'report.sales',
    group: 'Sales',
  },
  {
    type: 'CUSTOMER_HISTORY',
    name: 'Customer history',
    about: 'Every customer with what they have spent and how often.',
    dated: false,
    permission: 'report.sales',
    group: 'Sales',
  },
  {
    type: 'DAILY_EVENT_SHEET',
    name: 'Daily event sheet',
    about: 'The day’s events with halls, covers and timings.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
  },
  {
    type: 'KITCHEN_PRODUCTION_SHEET',
    name: 'Kitchen production',
    about: 'What the kitchen cooks on the From date, dish by dish, across every event '
      + 'on it. Reads the From date only; the To date does not narrow it.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
  },
  {
    type: 'STAFF_DEPLOYMENT',
    name: 'Staff deployment',
    about: 'Who is rostered where, and where the gaps are.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
  },
  {
    type: 'COMMUNICATIONS_SENT',
    name: 'Messages sent',
    about: 'Every WhatsApp, email and SMS sent to a customer, across every document — when it '
      + 'went, which channel, and whether it actually sent.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
    byCustomer: true,
    byOutlet: true,
    byChannel: true,
    statuses: [
      { code: 'QUEUED', label: 'Queued' },
      { code: 'SENDING', label: 'Sending' },
      { code: 'SENT', label: 'Sent' },
      { code: 'FAILED', label: 'Failed' },
      { code: 'SKIPPED', label: 'Skipped' },
      { code: 'READ', label: 'Read' },
    ],
  },
  {
    type: 'ORDER_STATUS_HISTORY',
    name: 'Order status history',
    about: 'Every status an order has passed through, one row a change, across the whole book '
      + 'rather than one order at a time — when it moved, who moved it, and what it was before.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
    byCustomer: true,
    byOutlet: true,
    byService: true,
    statuses: [
      { code: 'DRAFT', label: 'Draft' },
      { code: 'AWAITING_ADVANCE', label: 'Awaiting advance' },
      { code: 'CONFIRMED', label: 'Confirmed' },
      { code: 'PLANNING', label: 'Planning' },
      { code: 'READY_FOR_PRODUCTION', label: 'Ready for production' },
      { code: 'IN_PROGRESS', label: 'In progress' },
      { code: 'COMPLETED', label: 'Completed' },
      { code: 'BILLED', label: 'Billed' },
      { code: 'CLOSED', label: 'Closed' },
      { code: 'CANCELLED', label: 'Cancelled' },
    ],
  },
  {
    type: 'DOCUMENTS_AUDIT',
    name: 'Documents audit',
    about: 'Every order’s standing proforma and standing tax invoice against what the order '
      + 'itself totals to, with what it has been paid and still owes alongside it.',
    dated: true,
    permission: 'report.finance',
    group: 'Finance',
    byCustomer: true,
    byOutlet: true,
    byService: true,
    statuses: [
      { code: 'DRAFT', label: 'Draft' },
      { code: 'AWAITING_ADVANCE', label: 'Awaiting advance' },
      { code: 'CONFIRMED', label: 'Confirmed' },
      { code: 'PLANNING', label: 'Planning' },
      { code: 'READY_FOR_PRODUCTION', label: 'Ready for production' },
      { code: 'IN_PROGRESS', label: 'In progress' },
      { code: 'COMPLETED', label: 'Completed' },
      { code: 'BILLED', label: 'Billed' },
      { code: 'CLOSED', label: 'Closed' },
      { code: 'CANCELLED', label: 'Cancelled' },
    ],
  },
  {
    type: 'SWEET_ORDERS_DETAIL',
    name: 'Orders Details',
    about: 'The counter’s own register: every order, what was in it, who it went to, '
      + 'what the bill came to and what is still owed.',
    dated: true,
    permission: 'report.operations',
    group: 'Operations',
    byCustomer: true,
    byOutlet: true,
    byService: true,
    statuses: [
      { code: 'DRAFT', label: 'Draft' },
      { code: 'AWAITING_ADVANCE', label: 'Awaiting advance' },
      { code: 'CONFIRMED', label: 'Confirmed' },
      { code: 'PLANNING', label: 'Planning' },
      { code: 'READY_FOR_PRODUCTION', label: 'Ready for production' },
      { code: 'IN_PROGRESS', label: 'In progress' },
      { code: 'COMPLETED', label: 'Completed' },
      { code: 'BILLED', label: 'Billed' },
      { code: 'CLOSED', label: 'Closed' },
      { code: 'CANCELLED', label: 'Cancelled' },
    ],
  },
]

/** The URL a report is read at. Lower case, because a path is not a constant. */
export const reportPath = (type: string) => `/reports/${type.toLowerCase().replace(/_/g, '-')}`

export const reportByPath = (slug?: string) =>
  REPORTS.find((report) => reportPath(report.type) === `/reports/${slug}`)
